import GENERAL from '../constants/general';
import ROLES from 'core/Authorization/roles';
import { getTransactionFlowActions } from './transactionFlowHandler';

const { DIVISION_ID } = GENERAL;

const hasValue = value => value !== null && value !== undefined;

const getTransactionField = (transaction, snakeCaseKey, camelCaseKey) =>
	transaction && (transaction[snakeCaseKey] ?? transaction[camelCaseKey]);

export function hasReleasedOriginSignature(transaction) {
	const signed = transaction && transaction.signed;

	return Boolean(
		Array.isArray(signed) &&
			signed.some(signature => signature && signature.released === true),
	);
}

export function isDestinationTransactionOwner(transaction, profile) {
	const entity = profile && profile.entity;
	const destinationOwnerId = getTransactionField(
		transaction,
		'dwh_owner_id',
		'dwhOwnerId',
	);

	return Boolean(transaction && entity && destinationOwnerId === entity.id);
}

export function isOriginTransactionOwner(transaction, profile) {
	const entity = profile && profile.entity;
	const originOwnerId = getTransactionField(
		transaction,
		'owh_owner_id',
		'owhOwnerId',
	);

	return Boolean(transaction && entity && originOwnerId === entity.id);
}

export function canSignTransactionFromFeed(transaction, profile) {
	const flowActions = getTransactionFlowActions(transaction, profile);
	// Draft-aware inventory authorization is document-scoped and must be obtained
	// from the capabilities endpoint. Feed rows intentionally fail closed.
	if (flowActions.usesDraftFlow) return false;

	if (!canManageTransactionSignatures(transaction, profile)) return false;
	const completed = getTransactionField(
		transaction,
		'completed',
		'wtdIsClosed',
	);
	if (completed) return false;

	if (
		isDestinationTransactionOwner(transaction, profile) &&
		!isOriginTransactionOwner(transaction, profile)
	) {
		return hasReleasedOriginSignature(transaction);
	}

	return true;
}

/**
 * Dashboard returns legacy snake_case WTD fields, whereas Feed overview returns
 * camelCase metadata. Synapse ADM authorizes only entity administrators with L2.
 */
export function canManageTransactionSignatures(transaction, profile) {
	const user = profile && profile.user;
	const entity = profile && profile.entity;
	const userId = user && user.id;
	const roles = (user && user.roles) || {};
	const originOwnerId = getTransactionField(
		transaction,
		'owh_owner_id',
		'owhOwnerId',
	);
	const originOwnerDivisionId = getTransactionField(
		transaction,
		'owh_owner_division_id',
		'owhOwnerDivisionId',
	);
	const destinationOwnerId = getTransactionField(
		transaction,
		'dwh_owner_id',
		'dwhOwnerId',
	);
	const destinationOwnerDivisionId = getTransactionField(
		transaction,
		'dwh_owner_division_id',
		'dwhOwnerDivisionId',
	);
	const canReleaseAsOriginEntityAdministrator =
		originOwnerDivisionId === DIVISION_ID.ENTITY &&
		entity &&
		originOwnerId === entity.id &&
		roles[ROLES.INVENTORY.TRANSACTION.L2];
	const canAgreeAsDestinationEntityAdministrator =
		entity &&
		destinationOwnerDivisionId === DIVISION_ID.ENTITY &&
		destinationOwnerId === entity.id &&
		roles[ROLES.INVENTORY.TRANSACTION.L2];

	return Boolean(
		transaction &&
			user &&
			hasValue(userId) &&
			(canReleaseAsOriginEntityAdministrator ||
				canAgreeAsDestinationEntityAdministrator),
	);
}

export function hasTransactionSignature(transaction, profile) {
	const userId = profile && profile.user && profile.user.id;
	const signed = transaction && transaction.signed;

	return Boolean(
		hasValue(userId) &&
			Array.isArray(signed) &&
			signed.some(signature => signature && signature.user_id === userId),
	);
}

export function canCompleteTransaction(transaction, profile, entityDivisionId) {
	const completed = getTransactionField(
		transaction,
		'completed',
		'wtdIsClosed',
	);
	const destinationOwnerId = getTransactionField(
		transaction,
		'dwh_owner_id',
		'dwhOwnerId',
	);
	const destinationOwnerDivisionId = getTransactionField(
		transaction,
		'dwh_owner_division_id',
		'dwhOwnerDivisionId',
	);

	return Boolean(
		transaction &&
			!completed &&
			canManageTransactionSignatures(transaction, profile) &&
			destinationOwnerDivisionId === entityDivisionId &&
			profile &&
			profile.entity &&
			destinationOwnerId === profile.entity.id,
	);
}
